import type { Intent, IntentOutcome, Inventory } from './entry'
import { createRefreshQueue } from './refreshQueue'

/** One read of the session and the companies, or a session that answered `401`. */
export type Read = { kind: 'ok'; inventory: Inventory } | { kind: 'unauthenticated' }

export type Reconciled =
  | { status: 'ok'; inventory: Inventory; outcome: IntentOutcome | null }
  | { status: 'unauthenticated' }
  | { status: 'failed' }

interface Options {
  /** One read; `null` is the failure result. The signal aborts it at the deadline. */
  read: (signal: AbortSignal) => Promise<Read | null>
  /**
   * Applies one current read and the intents that share it in one update, and returns one
   * outcome per intent; `null` when the read is stale and was not applied.
   */
  commit: (inventory: Inventory, intents: Intent[]) => IntentOutcome[] | null
  /** Applies an unauthenticated read. */
  signedOut: () => void
  /** Applies a read that failed, or memberships that joined to nothing twice. */
  failed: (mismatch: boolean) => void
  deadlineMs: number
}

interface Pending {
  intent: Intent
  /** How many reads had started when the intent was registered. */
  after: number
  outcome?: IntentOutcome
}

type Answer = { kind: 'ok'; inventory: Inventory } | { kind: 'mismatch' } | { kind: 'unauthenticated' }

/**
 * `reconcile(intent?)`, the boundary's one coordinator over the session and the companies
 * (GRO-106 design, "Reconciliation"). It runs on the existing refresh queue, so reads are
 * serial and a call made while one is in flight shares one follow-up that starts after it.
 *
 * Each intent belongs to its caller until a read that **started after** the intent was
 * registered settles; that read applies it together with the inventory and every other
 * intent sharing the read, in one commit. An earlier read, or a routine one, never sees
 * it, so it cannot apply a transition from data older than the write that prompted it,
 * and cannot erase it either. Memberships that join to nothing keep the intents pending
 * for the one immediate follow-up; if that disagrees too, the intents end as failed.
 */
export function createReconciler({ read, commit, signedOut, failed, deadlineMs }: Options): (intent?: Intent) => Promise<Reconciled> {
  let started = 0
  const pending: Pending[] = []

  const queue = createRefreshQueue<{ seq: number; read: Read }, Answer>({
    request: async (signal) => {
      started += 1
      const seq = started
      const result = await read(signal)
      return result === null ? null : { seq, read: result }
    },
    apply: ({ seq, read: result }) => {
      if (result.kind === 'unauthenticated') {
        signedOut()
        return { kind: 'unauthenticated' }
      }
      const { inventory } = result
      if (inventory.clients.length === 0 && inventory.memberships.length > 0) return { kind: 'mismatch' }
      const sharing = pending.filter((p) => p.after < seq && p.outcome === undefined)
      const outcomes = commit(inventory, sharing.map((p) => p.intent))
      if (outcomes === null) return null
      sharing.forEach((p, index) => { p.outcome = outcomes[index] })
      return { kind: 'ok', inventory }
    },
    deadlineMs,
  })

  return async (intent?: Intent) => {
    const own: Pending | null = intent === undefined ? null : { intent, after: started }
    if (own !== null) pending.push(own)
    try {
      let answer = await queue()
      if (answer?.kind === 'mismatch') answer = await queue()
      if (answer === null || answer.kind === 'mismatch') {
        failed(answer !== null)
        return { status: 'failed' }
      }
      if (answer.kind === 'unauthenticated') return { status: 'unauthenticated' }
      return { status: 'ok', inventory: answer.inventory, outcome: own?.outcome ?? null }
    } finally {
      if (own !== null) pending.splice(pending.indexOf(own), 1)
    }
  }
}
